import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProject, PROJECT_INFO } from '../contexts/ProjectContext';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  LayoutDashboard,
  FolderKanban,
  Bot,
  Users,
  Send,
  Palette,
  Globe,
  CheckSquare,
  Inbox,
  DollarSign,
  Plug,
  Settings,
  FileText,
  MessageSquare,
  ChevronDown,
  LogOut,
  Menu,
  X,
  Bell
} from 'lucide-react';

const navItems = [
  { path: '/dashboard', label: 'Overview', icon: LayoutDashboard },
  { path: '/projects', label: 'Projects', icon: FolderKanban },
  { path: '/agents', label: 'Agents', icon: Bot },
  { path: '/leads', label: 'Leads', icon: Users },
  { path: '/outreach', label: 'Outreach', icon: Send },
  { path: '/queue', label: 'Approval Queue', icon: Inbox },
  { path: '/marketing', label: 'Marketing', icon: Palette },
  { path: '/website', label: 'Website Health', icon: Globe },
  { path: '/approvals', label: 'Approvals', icon: CheckSquare },
  { path: '/costs', label: 'Costs & Usage', icon: DollarSign },
  { path: '/integrations', label: 'Integrations', icon: Plug },
  { path: '/settings', label: 'Settings', icon: Settings, adminOnly: true },
  { path: '/audit', label: 'Audit Logs', icon: FileText }
];

export const AppLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const { projects, currentProject, projectInfo, switchProject, stats } = useProject();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const filteredNavItems = navItems.filter(item => !item.adminOnly || isAdmin);

  return (
    <div className="min-h-screen grid-mesh flex">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50 w-64 glass-panel flex flex-col
        transform transition-transform duration-300 lg:transform-none
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Project Switcher */}
        <div className="p-4 border-b border-white/5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className="w-full justify-between h-auto py-3 px-3 hover:bg-white/5"
                data-testid="project-switcher"
              >
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-xl"
                    style={{ backgroundColor: projectInfo.color + '20' }}
                  >
                    {projectInfo.icon}
                  </div>
                  <div className="text-left">
                    <div className="font-semibold">{projectInfo.name}</div>
                    <div className="text-xs text-muted-foreground">{projectInfo.region}</div>
                  </div>
                </div>
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuLabel>Switch Workspace</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {projects.map((project) => {
                const info = PROJECT_INFO[project.type] || PROJECT_INFO.kazilink;
                return (
                  <DropdownMenuItem
                    key={project.project_id}
                    onClick={() => switchProject(project.project_id)}
                    className={currentProject?.project_id === project.project_id ? 'bg-white/5' : ''}
                    data-testid={`project-option-${project.type}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-lg">{info.icon}</span>
                      <div>
                        <div className="font-medium">{project.name}</div>
                        <div className="text-xs text-muted-foreground">{info.region}</div>
                      </div>
                    </div>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto scrollbar-thin">
          {filteredNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={`sidebar-item ${isActive ? 'active' : ''}`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-sm font-medium">{item.label}</span>
                {item.path === '/approvals' && stats?.pending_approvals > 0 && (
                  <span className="ml-auto bg-amber-500/20 text-amber-400 text-xs px-1.5 py-0.5 rounded-full">
                    {stats.pending_approvals}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Chat Link */}
        <div className="p-3 border-t border-white/5">
          <Link
            to="/chat"
            onClick={() => setMobileOpen(false)}
            className={`sidebar-item ${location.pathname === '/chat' ? 'active' : ''}`}
            data-testid="nav-chat"
          >
            <MessageSquare className="w-5 h-5" />
            <span className="text-sm font-medium">Agent Chat</span>
          </Link>
        </div>

        {/* User */}
        <div className="p-3 border-t border-white/5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="w-full justify-start h-auto py-2 px-2 hover:bg-white/5">
                <div className="flex items-center gap-3">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-primary/20 text-primary text-xs">
                      {user?.name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-left">
                    <div className="font-medium text-sm truncate max-w-[140px]">{user?.name}</div>
                    <div className="text-xs text-muted-foreground capitalize">{user?.role}</div>
                  </div>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen lg:ml-0">
        {/* Mobile Header */}
        <header className="lg:hidden sticky top-0 z-30 glass-panel h-14 flex items-center justify-between px-4">
          <Button variant="ghost" size="icon" onClick={() => setMobileOpen(true)} data-testid="mobile-menu">
            <Menu className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <span className="text-lg">{projectInfo.icon}</span>
            <span className="font-semibold">{projectInfo.name}</span>
          </div>
          <Button variant="ghost" size="icon">
            <Bell className="w-5 h-5" />
          </Button>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-6 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
};
